/**
 * /blog/:slug — Individual blog post page
 * SEO: title, meta, og:tags, canonical, Article schema, FAQ schema
 */
import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Box, Typography, Chip, Divider, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getBlogPost } from "../data/blogPosts";

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogPost(slug);

  useEffect(() => {
    if (!post) return;
    document.title = post.metaTitle;

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    const setOg = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta("description", post.metaDescription);
    setOg("og:title", post.metaTitle);
    setOg("og:description", post.metaDescription);
    setOg("og:type", "article");

    let canon = document.querySelector("link[rel='canonical']");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `https://instacamgirls.com/blog/${post.slug}`;

    // Article schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.metaDescription,
      "datePublished": post.publishedAt,
      "publisher": {
        "@type": "Organization",
        "name": "InstaCamGirls",
        "url": "https://instacamgirls.com"
      },
      "mainEntityOfPage": `https://instacamgirls.com/blog/${post.slug}`
    };

    let schemaEl = document.getElementById("blog-post-schema");
    if (!schemaEl) {
      schemaEl = document.createElement("script");
      schemaEl.id = "blog-post-schema";
      schemaEl.type = "application/ld+json";
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify(schema);
  }, [post]);

  if (!post) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", px: 2, py: 8, textAlign: "center" }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Post not found</Typography>
        <Button component={Link} to="/blog" variant="contained">Back to Blog</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: 2, py: 4 }}>
      {/* Back link */}
      <Button
        startIcon={<ArrowBackIcon />}
        component={Link}
        to="/blog"
        sx={{ mb: 3, color: "#666" }}
      >
        All Posts
      </Button>

      {/* Header */}
      <Chip
        label={post.category}
        size="small"
        sx={{ mb: 2, backgroundColor: "#fce4ec", color: "#c2185b", fontWeight: 600, fontSize: 11 }}
      />
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1, lineHeight: 1.25 }}>
        {post.title}
      </Typography>
      <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
        <Typography sx={{ fontSize: 13, color: "#999" }}>
          {new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#999" }}>{post.readTime} min read</Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Content */}
      <Box sx={{ "& h2": { mt: 4, mb: 1.5, fontWeight: 700, fontSize: "1.3rem" } }}>
        {post.content.map((block, i) => {
          if (block.type === "intro") {
            return (
              <Typography key={i} sx={{ fontSize: "1.05rem", lineHeight: 1.8, mb: 3, color: "#222" }}>
                {block.text}
              </Typography>
            );
          }
          if (block.type === "h2") {
            return (
              <Typography key={i} variant="h5" component="h2" sx={{ fontWeight: 700, mt: 4, mb: 1.5 }}>
                {block.text}
              </Typography>
            );
          }
          if (block.type === "p") {
            return (
              <Typography key={i} sx={{ lineHeight: 1.8, mb: 2.5, color: "#333" }}>
                {block.text}
              </Typography>
            );
          }
          if (block.type === "list") {
            return (
              <Box key={i} sx={{ mb: 3 }}>
                {block.items.map((item) => (
                  <Box
                    key={item.href}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                      p: 2,
                      border: "1px solid #f0f0f0",
                      borderRadius: 1.5,
                      "&:hover": { borderColor: "#e91e63" },
                    }}
                  >
                    <Box>
                      <Typography
                        component={Link}
                        to={item.href}
                        sx={{ fontWeight: 600, color: "#e91e63", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#666", mt: 0.25 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          }
          if (block.type === "cta") {
            return (
              <Box key={i} sx={{ mt: 4, p: 3, background: "linear-gradient(135deg,#e91e63,#c2185b)", borderRadius: 2, textAlign: "center" }}>
                <Button
                  component={Link}
                  to={block.href}
                  variant="contained"
                  size="large"
                  sx={{ backgroundColor: "#fff", color: "#e91e63", fontWeight: 700, "&:hover": { backgroundColor: "#fce4ec" } }}
                >
                  {block.text}
                </Button>
              </Box>
            );
          }
          return null;
        })}
      </Box>

      <Divider sx={{ mt: 5, mb: 4 }} />

      {/* Footer nav */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>More Reading</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip label="All Posts" component={Link} to="/blog" clickable />
          <Chip label="Latina Cams" component={Link} to="/category/latina-cam-girls" clickable />
          <Chip label="MILF Cams" component={Link} to="/category/milf-cam-girls" clickable />
          <Chip label="Free Videos" component={Link} to="/videos" clickable />
        </Box>
      </Box>
    </Box>
  );
};

export default BlogPostPage;
